<template>
  <v-dialog
    v-model="localDialog"
    width="35%"
    @update:modelValue="updateDialog"
    transition=""
    persistent
    :open-delay="0"
    :close-delay="0"
  >
    <v-card>
      <v-card-title class="text-h6">
        <span>{{ isEntryMode ? "Assign Employee Weekend" : "Update Employee Weekend" }}</span>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-form @submit.prevent="submit" ref="EmployeeWeekendSetup">
          <v-row >
            <v-col cols="12">
              <v-autocomplete
                class="form-field-gap"
                label="Employee"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                :items="getHrmEmployeeList"
                item-title="name"
                item-value="id"
                v-model="employee_id.value.value"
                :error-messages="employee_id.errorMessage.value"
                clearable
                hide-details="auto"
                prepend-inner-icon="mdi-account"
              ></v-autocomplete>

              <v-select
                class="form-field-gap"
                clearable
                density="compact"
                chips
                label="Weekend Days"
                :items="getDaysList"
                item-title="days"
                item-value="id"
                v-model="day_ids.value.value"
                :error-messages="day_ids.errorMessage.value"
                multiple
                variant="outlined"
                bg-color="grey-lighten-4"
              />

              <v-date-input
                class="form-field-gap"
                label="Start Date"
                density="compact"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="start_date.value.value"
                :error-messages="start_date.errorMessage.value"
                autocomplete="off"
                clearable
              ></v-date-input>

              <v-date-input
                class="form-field-gap"
                label="End Date"
                density="compact"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="end_date.value.value"
                :error-messages="end_date.errorMessage.value"
                autocomplete="off"
                clearable
              ></v-date-input>

              <v-text-field
                class="form-field-gap"
                label="Remarks (Optional)"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="remarks.value.value"
                :error-messages="remarks.errorMessage.value"
                clearable
              ></v-text-field>

              <v-checkbox
                class="form-field-gap"
                label="Active"
                v-model="status.value.value"
                :error-messages="status.errorMessage.value"
                density="compact"
                variant="outlined"
                hide-details
                single-line
                color="primary"
                :true-value="'1'"
                :false-value="'0'"
              ></v-checkbox>
              <input v-model="weekend_employee_id.value.value" type="hidden" />
            </v-col>
          </v-row>

          <v-divider></v-divider>
          <v-card-actions>
            <v-layout class="justify-end">
              <v-btn class="bg-red" variant="flat" @click="closeDialog">
                Close
              </v-btn>
              <v-btn class="bg-brand w-15 ms-2" variant="flat" type="submit">
                {{ isEntryMode ? "Save" : "Update" }}
              </v-btn>
            </v-layout>
          </v-card-actions>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref } from "vue";
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";

export default {
  name: "EmployeeWeekendForm",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({
        weekend_employee_id: null,
        employee_id: "",
        day_ids: [],
        start_date: "",
        end_date: "",
        remarks: "",
        status: "1",
      }),
    },
  },
  data() {
    return {
      localDialog: this.dialog,
      isEntryMode: true,
    };
  },
  watch: {
    dialog(val) {
      this.localDialog = val;
    },
    localDialog(val) {
      this.$emit("update:dialog", val);
    },
    data: {
      handler(newData) {
        this.isEntryMode = !newData.weekend_employee_id;
        this.weekend_employee_id.setValue(
          newData.weekend_employee_id || "",
          false
        );
        this.employee_id.setValue(newData.employee_id || "", false);
        this.day_ids.setValue(newData.day_ids || [], false);
        this.start_date.setValue(newData.start_date || "", false);
        this.end_date.setValue(newData.end_date || "", false);
        this.remarks.setValue(newData.remarks || "", false);
        this.status.setValue(String(newData.status ?? "1"), false);
      },
      immediate: true,
      deep: true,
    },
  },
  created() {
    if (this.$store.getters.getHrmEmployeeList.length === 0) {
      this.$store.dispatch("getHrmEmployeeList");
    }
    if (this.$store.getters.getDaysList.length === 0) {
      this.$store.dispatch("getDaysList");
    }
  },
  computed: {
    getDaysList() {
      return this.$store.getters.getDaysList;
    },
    getHrmEmployeeList() {
      return this.$store.getters.getHrmEmployeeList;
    },
  },
  methods: {
    updateDialog(val) {
      this.$emit("update:dialog", val);
    },
    closeDialog() {
      this.$emit("update:dialog", false);
    },
  },
  setup(props, { emit }) {
    const store = useStore();
    const { handleSubmit, resetForm } = useForm({
      validationSchema: yup.object({
        employee_id: yup.string().required().label("Employee"),
        day_ids: yup.array().min(1).label("Weekend Days"),
        start_date: yup.date().required().label("Start Date"),
        end_date: yup
          .date()
          .nullable()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? null : value
          )
          .min(yup.ref("start_date"), "End Date cannot be before Start Date")
          .label("End Date"),
        remarks: yup.string().max(250).nullable().trim().label("Remarks"),
        status: yup.string().oneOf(["0", "1"]).label("Status"),
      }),
    });

    const weekend_employee_id = useField("weekend_employee_id");
    const employee_id = useField("employee_id");
    const day_ids = useField("day_ids");
    const start_date = useField("start_date");
    const end_date = useField("end_date");
    const remarks = useField("remarks");
    const status = useField("status");
    const loading = ref(false);

    const submit = handleSubmit((values) => {
      loading.value = true;
      values.start_date = formattedDate(start_date.value.value);
      values.end_date = formattedDate(end_date.value.value);
      store
        .dispatch("saveUpdateEmployeeWeekend", values)
        .then((response) => {
          toast.success(response.data.message || "Weekend saved successfully");
          resetForm();
          emit("update:dialog", false);
          return store.dispatch("getEmployeeWeekendList");
        })
        .catch((error) => {
          toast.error(error.response?.data?.message || "Something went wrong");
        })
        .finally(() => {
          loading.value = false;
        });
    });

    return {
      submit,
      weekend_employee_id,
      employee_id,
      day_ids,
      start_date,
      end_date,
      remarks,
      status,
      loading,
    };
  },
};
</script>

<style scoped>
.form-field-gap {
  margin-bottom: 16px;
}
</style>
